import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import MobileAppManagementAPI from '@/features/mobile-app/api/MobileAppManagementAPI'
import { QUERY } from '@/features/mobile-app/api/MobileAppManagementAPI.model'
import { ErrorModel } from '@/shared/api/BaseAPI.model'
import ImageUploadButton from '@/shared/components/ImageUploadButton/ImageUploadButton'
import Skeleton from '@/shared/components/Skeleton/Skeleton'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import IMAGE_SLOTS_KEYS from '@/shared/store/imageSlots.model'
import { useImageSlots } from '@/shared/store/useImageSlots'

const LOGO_SLOT = IMAGE_SLOTS_KEYS.RESTAURANT_LOGO

/** Both null until a logo is uploaded for the first time. */
interface StoredLogo {
  imageUrl: string | null
  version: number | null
}

/**
 * The venue's logo, saved as soon as it is cropped.
 *
 * Unlike every other fact in these settings it already lives on the backend
 * (`/restaurants/{id}/logo`), which is where the guest app reads it from. This
 * is the only place it is set; the mobile-app preview and the table cards read
 * the same query, so a new logo shows there without a reload.
 *
 * It can be replaced but not removed: the backend has no way to clear it.
 */
function RestaurantLogoField() {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const logo = useQuery<StoredLogo, ErrorModel>({
    queryKey: [QUERY.LOGO],
    queryFn: MobileAppManagementAPI.getLogoImage
  })
  // Every upload lands at the same address; the version is what changes, so
  // it goes into the query string or the browser keeps the old picture.
  const savedUrl = logo.data?.imageUrl
    ? `${logo.data.imageUrl}?t=${logo.data.version}&alt=media`
    : undefined

  const slot = useImageSlots(s => s.slots[LOGO_SLOT])
  const clearSlot = useImageSlots(s => s.clear)
  const croppedLogo = slot?.croppedBlob ?? null

  // A crop left behind would upload again the next time this opens.
  useEffect(() => () => clearSlot(LOGO_SLOT), [clearSlot])

  useEffect(() => {
    if (!croppedLogo) return
    const upload = async () => {
      try {
        const formData = new FormData()
        formData.append(
          'file',
          new File([croppedLogo], 'logo', {
            type: slot?.mime || 'image/png'
          })
        )
        await MobileAppManagementAPI.setLogoImage(formData)
        // Read back before the crop is dropped, so the tile goes from the crop
        // to the stored copy of it rather than through the old logo.
        await queryClient.invalidateQueries({ queryKey: [QUERY.LOGO] })
      } catch {
        showSnackbar(
          t('ALERTS.POST_LOGO_IMAGE_UPLOAD_ERROR.TITLE'),
          t('ALERTS.POST_LOGO_IMAGE_UPLOAD_ERROR.DESCRIPTION'),
          'error'
        )
      } finally {
        clearSlot(LOGO_SLOT)
      }
    }
    upload()
    // Keyed on the crop alone: a new one is the only thing that uploads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [croppedLogo])

  // Shaped like the tile, so the empty "add a logo" circle never flashes up
  // in front of a logo that is on its way.
  if (logo.isPending) return <Skeleton width="8rem" height="9.5rem" />

  return (
    <ImageUploadButton
      imageShape="circle"
      label={t('RESTAURANT_SETTINGS.VENUE.LOGO')}
      slotId={LOGO_SLOT}
      imageUrl={savedUrl}
      // A circle, because the guest app shows the logo in one.
      imageSize={{ width: 440, height: 440 }}
      stencilSize={{ width: 264, height: 264 }}
      isDeletable={false}
    />
  )
}

export default RestaurantLogoField
